import { Trip } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  PlusIcon, 
  Calendar, 
  ExternalLink, 
  Plane, 
  MapPin,
  User 
} from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { format, differenceInDays } from "date-fns";
import { useLocation } from "wouter";

type TripsPanelProps = {
  trips: Trip[];
  clientId: number;
};

const toCountryList = (countries: Trip["countries"]): string[] => {
  if (Array.isArray(countries)) {
    return countries
      .filter((country): country is string => typeof country === "string")
      .map((country) => country.trim())
      .filter(Boolean);
  }

  if (typeof countries === "string") {
    const trimmed = countries.trim();
    return trimmed ? [trimmed] : [];
  }

  return [];
};

const toDate = (value: Trip["startDate"] | Trip["endDate"]): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

export function TripsPanel({ trips, clientId }: TripsPanelProps) {
  const [isAssociateDialogOpen, setIsAssociateDialogOpen] = useState(false);
  const [_, setLocation] = useLocation();

  // TODO: Implement associate trip mutation

  function handleAssociateTrip() {
    setIsAssociateDialogOpen(true);
  }
  
  function handleViewTrip(tripId: number) {
    setLocation(`/trips/${tripId}`);
  }
  
  function handleCreateTrip() {
    setLocation("/trips/new");
  }
  
  function getTripDateDisplay(trip: Trip) {
    const start = toDate(trip.startDate);
    const end = toDate(trip.endDate);

    if (!start || !end) {
      return (
        <span className="flex items-center gap-1">
          <Calendar className="h-4 w-4" />
          <span>Dates not set</span>
        </span>
      );
    }

    const diff = differenceInDays(end, start);
    const days = Number.isNaN(diff) ? null : Math.max(diff + 1, 1);
    
    return (
      <span className="flex items-center gap-1">
        <Calendar className="h-4 w-4" />
        {format(start, 'MMM d')} - {format(end, 'MMM d, yyyy')}
        {days && (
          <span className="text-muted-foreground">({days} {days === 1 ? 'day' : 'days'})</span>
        )}
      </span>
    );
  }
  
  function getCountriesDisplay(trip: Trip) {
    const countries = toCountryList(trip.countries);
    if (countries.length === 0) return null;

    return (
      <div className="flex items-center gap-1 text-sm">
        <MapPin className="h-4 w-4 text-muted-foreground" />
        <span>{countries.join(', ')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Associated Trips</h3>
        <div className="space-x-2">
          <Button variant="outline" size="sm" onClick={handleAssociateTrip}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Associate Trip
          </Button>
          <Button size="sm" onClick={handleCreateTrip}>
            <Plane className="h-4 w-4 mr-2" />
            Create Trip
          </Button>
        </div>
      </div>

      {trips.length === 0 ? (
        <Alert>
          <AlertDescription>
            This contact is not associated with any trips yet.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {trips.map((trip) => (
            <Card 
              key={trip.id} 
              className="cursor-pointer hover:bg-accent/5 transition-colors"
              onClick={() => handleViewTrip(trip.id)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium">{trip.name ?? 'Untitled Trip'}</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewTrip(trip.id);
                    }}
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="sr-only">View trip</span>
                  </Button>
                </div>
                
                {getTripDateDisplay(trip)}
                {getCountriesDisplay(trip)}
                
                {trip.clientName && (
                  <div className="flex items-center gap-1 text-sm mt-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Client: {trip.clientName}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Associate Trip Dialog */}
      <Dialog open={isAssociateDialogOpen} onOpenChange={setIsAssociateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Associate with Existing Trip</DialogTitle>
            <DialogDescription>
              Associate this contact with an existing trip in your system.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Form will be implemented here to associate the contact with existing trips.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
